import React from 'react';
import type { PropsWithChildren } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { buildSmartAppBannerContent } from 'components/marketplace/helpers/appDeepLink';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import {
  getSchemaOfferPrice,
  getSeoFields,
  getSeoListingFraming,
  isSoldOrUnavailableProperty,
} from 'components/properties/helpers/property';
import SeoHead from 'components/properties/shared/SeoHead';
import {
  CONTENT_PAGE_FONT_PRELOADS,
  COSMOS_ASSET_ORIGIN,
} from 'components/shared/performance/preloads';
import StaticSiteHeader from 'components/static-site/StaticSiteHeader';
import Head from 'next/head';

const MarketplaceLayout: React.FC<
  PropsWithChildren<{ property?: PropertyDetailsType; fullPageScroll?: boolean }>
> = ({ children, property, fullPageScroll = false }) => {
  // A list-safe summary can keep a degraded PDP readable, but structured data
  // must not advertise an offer or self-tour without current availability.
  const soldOrUnavailable = property ? isSoldOrUnavailableProperty(property) : false;
  const framing = property ? getSeoListingFraming(property) : 'for-sale';
  const schemaOfferPrice = property?.hasCurrentAvailability ? getSchemaOfferPrice(property) : null;
  const hasSelfTour = property?.hasCurrentAvailability && property.canSelfTour === true;

  const { coverImage, readableAddress, title, canonicalUrl, metaDescription } = getSeoFields({
    addressId: property?.addressToken,
    displaySlug: property?.slug,
    framing,
    street: property?.street,
    unitType: property?.unitType,
    unitNumber: property?.unitNumber,
    city: property?.city,
    state: property?.state,
    postalCode: property?.zip,
    bedrooms: property?.bedrooms,
    bathrooms: property?.bathrooms,
    sqFtTotalLiving: property?.sqFtTotalLiving,
    coverImageProp: property?.photos?.[0],
  });
  const saleNoun = soldOrUnavailable ? 'home' : 'home for sale';
  const fallbackImageAlt = soldOrUnavailable ? 'Home on Opendoor' : 'Home for sale on Opendoor';
  const imageAlt = readableAddress
    ? `Photo of ${saleNoun} at ${readableAddress}`
    : fallbackImageAlt;
  const seoHead = (
    <SeoHead
      base={{
        title,
        author: 'Opendoor, Inc.',
        metaDescription,
        canonicalUrl,
      }}
      og={{
        url: canonicalUrl,
        title,
        description: metaDescription,
        siteName: 'Opendoor',
        image: coverImage,
        imageAlt,
        type: 'website',
      }}
      schema={{
        name: readableAddress,
        description: metaDescription,
        url: canonicalUrl,
        address: {
          ...(property?.street && {
            streetAddress:
              formatStreetAddress(property.street, property.unitType, property.unitNumber) ||
              undefined,
          }),
          ...(property?.city && { addressLocality: property.city }),
          ...(property?.state && { addressRegion: property.state }),
          ...(property?.zip && { postalCode: property.zip }),
        },
        ...(property?.sqFtLot && {
          floorSize: {
            value: property.sqFtLot.toString(),
          },
        }),
        ...(property?.yearBuilt && {
          yearBuilt: property.yearBuilt.toString(),
        }),
        ...(property?.bedrooms && {
          numberOfBedrooms: property.bedrooms.toString(),
        }),
        ...(property?.bathrooms && {
          numberOfBathroomsTotal: property.bathrooms.toString(),
        }),
        ...(schemaOfferPrice && { listPrice: schemaOfferPrice }),
        image: coverImage,
        lat: property?.latitude ?? undefined,
        lon: property?.longitude ?? undefined,
        ...(hasSelfTour && { hasSelfTour }),
        addressToken: property?.addressToken,
      }}
    />
  );
  return (
    <>
      <Head>
        {CONTENT_PAGE_FONT_PRELOADS.map((preload) => (
          <link rel="preload" key={preload.href} {...preload} />
        ))}
        <link rel="preconnect" href="https://images.opendoor.com" />
        <link rel="dns-prefetch" href="https://images.opendoor.com" />
        {COSMOS_ASSET_ORIGIN && (
          <link rel="preconnect" href={COSMOS_ASSET_ORIGIN} crossOrigin="anonymous" />
        )}
        {COSMOS_ASSET_ORIGIN && <link rel="dns-prefetch" href={COSMOS_ASSET_ORIGIN} />}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
        {property?.addressToken && (
          <meta
            name="apple-itunes-app"
            content={buildSmartAppBannerContent({ listingUrl: canonicalUrl })}
          />
        )}
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="https://www.opendoor.com/apple-touch-icon-144.png"
        />
      </Head>
      {seoHead}
      <Col className="bg-od-bg-primary">
        <StaticSiteHeader slug="homes" />
        <Box
          as="main"
          tabIndex={-1}
          className={
            fullPageScroll
              ? 'overflow-x-clip bg-od-bg-primary'
              : 'overflow-x-hidden bg-od-bg-primary'
          }
        >
          {children}
        </Box>
      </Col>
    </>
  );
};

export default MarketplaceLayout;
